import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import api from '../utils/authUtils';

const Wrapper = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SubmitButton = styled.button`
  background-color: #47ABDB;
  color: white;
  border: none;
  padding: 10px 20px;
  width: 100%;
  font-size: 1rem;
  cursor: pointer;
  border-radius: 0px;

  &:hover { background-color: #0056b3; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ErrorMessage = styled.p`
  color: red;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 10px;
  border-radius: 4px;
  text-align: center;
`;

// Shown instead of the full RegistrationForm when the visitor is already
// logged in - their profile is already on file, so registering for another
// event is just a confirmation, not a form to fill in again.
const AttendeeEventRegisterConfirm = ({ eventInfo, onSubmitSuccess, registrationSuccess }) => {
  const intl = useIntl();
  const t = (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (registrationSuccess) {
    return (
      <Wrapper>
        <SuccessMessage>
          {eventInfo?.requiresApproval
            ? t('registration.preSuccess', 'Pre-registration successful! You will receive a confirmation email once your spot has been approved.')
            : t('registration.success', 'Registration successful! Thank you for registering.')}
        </SuccessMessage>
      </Wrapper>
    );
  }

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setError('');
    try {
      await api.post(`/api/attendee/events/${eventInfo.id}/register`);
      onSubmitSuccess();
    } catch (err) {
      if (err.response?.data?.error === 'EMAIL_NOT_VERIFIED') {
        setError(t('registration.emailNotVerified', 'Please verify your email address first - check your inbox, or visit your profile to resend the verification email.'));
      } else if (typeof err.response?.data === 'string' && err.response.data.includes('Already registered')) {
        setError(t('registration.alreadyRegistered', 'You are already registered for this event.'));
      } else if (typeof err.response?.data === 'string' && err.response.data.trim()) {
        // Surface the backend's real reason (e.g. "No attendee profile
        // linked to this account") instead of a generic failure.
        setError(err.response.data);
      } else {
        setError(t('registration.error', 'Registration failed. Please try again.'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Wrapper>
      <p>
        {t('registration.confirmPrompt', 'Register for this event using your saved profile details?')}
      </p>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <SubmitButton type="button" onClick={handleConfirm} disabled={isSubmitting}>
        {isSubmitting
          ? t('registration.registering', 'Registering…')
          : (eventInfo?.requiresApproval ? t('form.preRegister', 'Pre-register') : t('form.register', 'Register'))}
      </SubmitButton>
    </Wrapper>
  );
};

export default AttendeeEventRegisterConfirm;
